import { AxiosError } from 'axios';
import { FormikHelpers, FormikProps } from 'formik';
import { filter } from 'lodash';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { internalWalletsApi, shopsApi, usersApi } from 'api';
import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikRadioGroup,
  ShopSelect,
  UserSelect,
  FormikDialog,
  DataWrapper,
} from 'components';
import { QueryKey, UserRole } from 'enums';
import { useMutation, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { InternalWallet } from 'types';
import { validationUtils, walletsUtils } from 'utils';

type Values = {
  role: UserRole;
  userId?: string;
  shopId?: string;
};

type Props = DialogProps<InternalWallet>;

export const InternalUserWalletDetailsDialog: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets.internal_user_wallet_dialog',
  });
  const { t: tCommon } = useTranslation();
  const queryClient = useQueryClient();
  const { role } = useUser();

  const queryResultTraders = useQuery(
    QueryKey.UsersTraders,
    usersApi.getAllTraders,
    { enabled: open },
  );
  const queryResultMerchants = useQuery(
    QueryKey.UsersMerchants,
    usersApi.getAllMerchants,
    { enabled: open },
  );
  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllNotArchiveAsRole(role),
    { enabled: open },
  );

  const initialValues: Values = useMemo(
    () => ({
      role: UserRole.Trader,
      userId: '',
      shopId: '',
    }),
    [],
  );

  const mutationCreate = useMutation<InternalWallet, AxiosError, Values>(
    (values) =>
      internalWalletsApi.createUserInternalWallet({
        userId: values.userId!,
        shopId: values.role === UserRole.Merchant ? values.shopId : undefined,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.InternalUsersWallets);
      },
      notifierMessages: {
        error: walletsUtils.getErrorMessage,
      },
    },
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      formikHelpers.setSubmitting(true);
      mutationCreate.mutate(values, {
        onSuccess: () => {
          queryClient.invalidateQueries(QueryKey.InternalUsersWallets);
          formikHelpers.resetForm();
          onClose({ ok: true });
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
      });
    },
    [mutationCreate, queryClient, onClose],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data?.values, result.data?.formikHelpers);
      } else {
        onClose(result);
      }
    },
    [handleSubmit, onClose],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        role: Yup.mixed<UserRole>()
          .oneOf([UserRole.Trader, UserRole.Merchant])
          .required(tCommon('errors.required')),
        userId: Yup.string().when('role', {
          is: UserRole.Trader,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        shopId: Yup.string().when('role', {
          is: UserRole.Merchant,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [tCommon],
  );

  const handleUserChange = useCallback((formik: FormikProps<Values>) => {
    formik.setFieldValue('shopId', '');
    formik.setFieldTouched('shopId', false);
    setTimeout(() => {
      formik.validateForm();
    });
  }, []);

  const handleRoleChange = useCallback(
    (formik: FormikProps<Values>) => {
      formik.setFieldValue('userId', '');
      formik.setFieldTouched('userId', false);
      handleUserChange(formik);
    },
    [handleUserChange],
  );

  return (
    <FormikDialog
      formikProps={{
        initialValues,
        validationSchema,
        enableReinitialize: true,
        validateOnMount: true,
      }}
      dialogProps={{
        open,
        title: t('title'),
        disabled: mutationCreate.isLoading,
        onClose: handleClose,
      }}
    >
      {({ formik }) => (
        <DataWrapper
          isLoading={
            mutationCreate.isLoading ||
            queryResultMerchants.isLoading ||
            queryResultShops.isLoading ||
            queryResultTraders.isLoading
          }
        >
          {/* TODO: workaround to increase modal height. See SPAY-3683 */}
          <FormControls sx={{ marginBottom: '2rem' }}>
            <FormikRadioGroup
              name="role"
              label={t('fields.role')}
              disabled={mutationCreate.isLoading}
              options={[
                { value: UserRole.Trader, label: t('fields.trader') },
                { value: UserRole.Merchant, label: t('fields.merchant') },
              ]}
              onChange={() => {
                handleRoleChange(formik);
              }}
            />
            <FormControls>
              <UserSelect
                name="userId"
                label={t('fields.user')}
                disabled={mutationCreate.isLoading}
                users={
                  formik.values.role === UserRole.Trader
                    ? queryResultTraders.data
                    : queryResultMerchants.data
                }
                onChange={() => {
                  handleUserChange(formik);
                }}
              />
              {formik.values.role === UserRole.Merchant &&
                formik.values.userId && (
                  <ShopSelect
                    name="shopId"
                    label={t('fields.shop')}
                    disabled={mutationCreate.isLoading}
                    shops={filter(
                      queryResultShops.data,
                      (shop) => shop.userId === formik.values.userId,
                    )}
                  />
                )}
            </FormControls>
          </FormControls>
        </DataWrapper>
      )}
    </FormikDialog>
  );
};
